import {
  Accordion,
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  Flex,
  Grid,
  Image,
  Rating,
  Skeleton,
  Table,
  Text,
  Textarea,
} from '@mantine/core';
import { AxiosResponse, isAxiosError } from 'axios';
import { useEffect, useReducer, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { addIcon, DummyImage, locationIcon, removeIcon, stepBackColor, stepForwardColor } from '../../assets';
import { toastError } from '../../config/Helper';
import { ROUTES } from '../../config/RoutesName';
import { useGlobalContext } from '../../context/global-context';
import { cartItemService } from '../../services/cart-item/cart-item.service';
import { useGetExtraItems } from '../../services/extra-items/extra-items.service';
import GreenDot from '../RestaurantDetails/GreenDot';
import RedDot from '../RestaurantDetails/RedDot';
import YellowDot from '../RestaurantDetails/YellowDot';
import { useAddToCartRefactoredStyles } from './style';
import { IBaseResponseError } from '../../typescript/interfaces/common.interface';

type Action =
  | {
      type: 'FOOD_ITEM_INCREMENT' | 'FOOD_ITEM_DECREMENT';
      payload: {
        quantity: number;
      };
    }
  | {
      type: 'INITIAL_PRICE_CALCULATE';
      payload: {
        unitPrice: number;
      };
    }
  | {
      type: 'ADD_EXTRA_ITEM';
      payload: {
        amount: number;
        index: number;
      };
    }
  | {
      type: 'REMOVE_EXTRA_ITEM';
      payload: {
        amount: number;
        index: number;
      };
    }
  | {
      type: 'CHANGE_EXTRA_ITEM_WHILE_CHECKED';
      payload: {
        amount: number;
      };
    };

type TotalPriceState = {
  unitPrice: number; // this holds the price of the main food item
  totalPrice: number;
  quantity: number;
  totalPriceWithoutAddedItem: number;
  addedItems: Set<number>;
};

const initialTotalPriceState: TotalPriceState = {
  unitPrice: 0,
  totalPrice: 0,
  quantity: 0,
  totalPriceWithoutAddedItem: 0,
  addedItems: new Set(),
};

class StateUpdater<T> {
  private readonly state: T;

  constructor(state: T) {
    this.state = { ...state };
  }

  update<K extends keyof T & string>(key: K, value: T[K]): this {
    if (this.state[key] !== value) {
      this.state[key] = value;
    }
    return this;
  }

  getState(): T {
    return { ...this.state };
  }
}

const reducer = (state: TotalPriceState, action: Action): TotalPriceState => {
  const stateUpdater = new StateUpdater(state);

  switch (action.type) {
    case 'INITIAL_PRICE_CALCULATE': {
      // return {
      //   ...state,
      //   totalPrice: action.payload.unitPrice,
      //   quantity: 1,
      //   totalPriceWithoutAddedItem: action.payload.unitPrice,
      //   unitPrice: action.payload.unitPrice,
      // };
      return stateUpdater
        .update('totalPrice', action.payload.unitPrice)
        .update('quantity', 1)
        .update('totalPriceWithoutAddedItem', action.payload.unitPrice)
        .update('unitPrice', action.payload.unitPrice)
        .getState();
    }

    case 'FOOD_ITEM_INCREMENT': {
      const newTotalPrice = state.totalPrice + state.unitPrice * action.payload.quantity;
      const newQuantity = state.quantity + action.payload.quantity;

      // return {
      //   ...state,
      //   totalPrice: newTotalPrice,
      //   totalPriceWithoutAddedItem: newTotalPrice,
      //   quantity: newQuantity,
      // };
      return stateUpdater
        .update('totalPrice', newTotalPrice)
        .update('totalPriceWithoutAddedItem', newTotalPrice)
        .update('quantity', newQuantity)
        .getState();
    }

    case 'FOOD_ITEM_DECREMENT': {
      const newTotalPrice = state.totalPrice - state.unitPrice * action.payload.quantity;
      const newQuantity = state.quantity === 1 ? state.quantity : state.quantity - action.payload.quantity;

      // return {
      //   ...state,
      //   totalPrice: newTotalPrice,
      //   totalPriceWithoutAddedItem: newTotalPrice,
      //   quantity: newQuantity,
      // };

      return stateUpdater
        .update('totalPrice', newTotalPrice)
        .update('totalPriceWithoutAddedItem', newTotalPrice)
        .update('quantity', newQuantity)
        .getState();
    }

    case 'ADD_EXTRA_ITEM': {
      const newTotalPrice = state.totalPrice + action.payload.amount;
      const previousSet = state.addedItems;

      const s = new Set(previousSet);
      s.add(action.payload.index);

      // return {
      //   ...state,
      //   totalPrice: newTotalPrice,
      //   addedItems: s,
      // };
      return stateUpdater.update('totalPrice', newTotalPrice).update('addedItems', s).getState();
    }

    case 'REMOVE_EXTRA_ITEM': {
      const newTotalPrice = state.totalPrice - action.payload.amount;

      const previousSet = state.addedItems;
      const s = new Set(previousSet);
      s.delete(action.payload.index);

      // return {
      //   ...state,
      //   totalPrice: newTotalPrice,
      //   addedItems: s,
      // };
      return stateUpdater.update('totalPrice', newTotalPrice).update('addedItems', s).getState();
    }

    case 'CHANGE_EXTRA_ITEM_WHILE_CHECKED': {
      // return {
      //   ...state,
      //   totalPrice: state.totalPriceWithoutAddedItem + action.payload.amount,
      // };

      return stateUpdater.update('totalPrice', state.totalPriceWithoutAddedItem + action.payload.amount).getState();
    }
  }
};

const AddToCartRefactored = () => {
  const { state: RouterState } = useLocation();

  const { classes, cx } = useAddToCartRefactoredStyles();

  const { data, isLoading, isSuccess, refetch } = useGetExtraItems({
    menuItemId: RouterState?.foodInfo.menu_item_id,
    enabledIf: true,
  });

  const { cookies, setCookie } = useGlobalContext();

  const navigate = useNavigate();

  const [totalPrice, dispatch] = useReducer(reducer, initialTotalPriceState);

  const [requestToChefNote, setRequestToChefNote] = useState('');

  useEffect(() => {
    dispatch({ type: 'INITIAL_PRICE_CALCULATE', payload: { unitPrice: RouterState?.foodInfo.price } });
  }, []);

  type ExtraItem = Record<number, { currentValue: number; maxValue: number; id: number }>;

  const [record, setRecord] = useState<ExtraItem>({});

  useEffect(() => {
    let obj: ExtraItem = {};

    data?.item_choices.forEach((value, index) => {
      const maximumValue = data.item_choices[index].addon_levels.length;
      const id = data.item_choices[index].addon_levels[0].addon_level_id;
      obj = {
        ...obj,
        [index]: {
          id: id,
          currentValue: 0,
          maxValue: maximumValue,
        },
      };
    });

    setRecord(obj);
  }, [data]);

  const handleCheckBoxClick = ({
    e,
    index,
    amount,
  }: {
    e: React.ChangeEvent<HTMLInputElement>;
    index: number;
    amount: number;
  }) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      dispatch({
        type: 'ADD_EXTRA_ITEM',
        payload: {
          amount: amount,
          index: index,
        },
      });
    } else {
      dispatch({
        type: 'REMOVE_EXTRA_ITEM',
        payload: {
          amount: amount,
          index: index,
        },
      });
    }
  };

  const [addToCardIsLoading, setAddToCardIsLoading] = useState(false);

  const handleAdd = async () => {
    setAddToCardIsLoading(true);
    if (data?.is_in_cart) {
      navigate(ROUTES.CART);
    } else {
      const addon_level_ids: number[] = [];

      Object.entries(record).forEach((data) => {
        if (totalPrice.addedItems.has(+data[0])) {
          addon_level_ids.push(data[1].id);
        }
      });

      const objectToSend = {
        restaurant_id: RouterState?.foodInfo?.restaurant_id,
        menu_item_id: RouterState?.foodInfo?.menu_item_id,
        quantity: totalPrice.quantity,
        addon_level_ids: addon_level_ids,
        note: requestToChefNote,
      };

      try {
        const response = await cartItemService(objectToSend);
        setAddToCardIsLoading(false);
        refetch();
        let previousCartItemCount = cookies['cart-item-count'];
        let previousCartItemPrice = cookies['cart-item-price'];
        const previousRestaurantIdIfAny = cookies['current-restaurant-id'];

        if (previousRestaurantIdIfAny) {
          /**
           * If we already have added an item from a restaurant
           */
          if (+previousRestaurantIdIfAny !== +RouterState?.restaurantInfo?.restaurantId) {
            /**
             * If the item we are trying to add is not from the same restaurant
             */
            setCookie('current-restaurant-id', RouterState?.restaurantInfo?.restaurantId);
            setCookie('cart-item-count', +response?.data?.quantity);
            setCookie('cart-item-price', +totalPrice.totalPrice);
          } else {
            /**
             * If the item we are trying to add is from the same restaurant
             */
            setCookie('cart-item-count', parseInt(previousCartItemCount) + response?.data?.quantity);
            setCookie('cart-item-price', parseFloat(previousCartItemPrice) + totalPrice.totalPrice);
          }
        } else {
          /**
           * If this is the first time we are adding an item to the cart
           */
          setCookie('current-restaurant-id', RouterState?.foodInfo?.restaurant_id);
          setCookie('cart-item-count', response?.data?.quantity);
          setCookie('cart-item-price', totalPrice.totalPrice);
        }

        // if (previousCartItemCount && previousCartItemPrice) {
        //   if (isNaN(parseInt(previousCartItemCount)) || isNaN(parseInt(previousCartItemPrice))) {
        //     throw new Error('could not parse cookies');
        //   }

        //   setCookie('cart-item-count', parseInt(previousCartItemCount) + response?.data?.quantity);
        //   setCookie('cart-item-price', parseInt(previousCartItemPrice) + totalPrice.totalPrice);
        // } else {
        //   setCookie('cart-item-count', response?.data?.quantity);
        //   setCookie('cart-item-price', totalPrice.totalPrice);
        // }

        navigate(ROUTES.DETAILS + `/${RouterState.foodInfo.restaurant_id}`, {
          state: {
            restaurantInfo: RouterState?.restaurantInfo,
          },
        });
      } catch (error: unknown) {
        if (isAxiosError(error)) {
          const response = error.response as AxiosResponse<IBaseResponseError> | undefined;
          const data = response?.data;
          const hasApiError = data?.data?.error_type && data?.data?.error_message;
          // toastError(hasApiError ? data : data?.message);
        }
        setAddToCardIsLoading(false);
      }
    }
  };

  const handleFoodQuantityChange = (type: 'increase' | 'decrease', byAmount: number) => {
    switch (type) {
      case 'increase':
        dispatch({
          type: 'FOOD_ITEM_INCREMENT',
          payload: {
            quantity: byAmount,
          },
        });

        break;
      case 'decrease':
        dispatch({
          type: 'FOOD_ITEM_DECREMENT',
          payload: {
            quantity: byAmount,
          },
        });
        break;
    }
  };

  const handleChange = (index: number, type: 'increase' | 'decrease', price: number, id: number) => {
    if (totalPrice.addedItems.has(index)) {
      dispatch({
        type: 'CHANGE_EXTRA_ITEM_WHILE_CHECKED',
        payload: {
          amount: price,
        },
      });
    }

    switch (type) {
      case 'increase':
        setRecord((prev) => {
          const maximumValue = prev[index].maxValue;
          const currentValue = prev[index].currentValue;

          return {
            ...prev,
            [index]: {
              ...prev[index],
              id: id,
              currentValue: currentValue + 1 >= maximumValue ? currentValue : currentValue + 1,
            },
          };
        });

        break;
      case 'decrease':
        setRecord((prev) => {
          const currentValue = prev[index].currentValue;
          return {
            ...prev,
            [index]: { ...prev[index], id: id, currentValue: currentValue - 1 < 0 ? currentValue : currentValue - 1 },
          };
        });
        break;
    }
  };

  return (
    <>
      <Container>
        <Box
          style={{
            borderRadius: 16,
            padding: 16,
            background: 'rgba(255, 255, 255, 0.75)',
            backdropFilter: 'blur(9px)',
            boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.15)',
          }}>
          <Flex direction='column' gap={16}>
            <Text c='magenta.9' fz={24} fw={500} lh={1}>
              {RouterState?.restaurantInfo.restaurantName}
            </Text>

            <Flex gap={4} align='center'>
              <Rating readOnly fractions={4} value={Number(RouterState?.restaurantInfo.rating)} />
              <Text fz={14} fw={500} lh={1}>
                ({RouterState?.restaurantInfo.ratingCount})
              </Text>
            </Flex>

            <Flex align='center' gap={4}>
              <Image src={locationIcon} width={14} height={14} fit='cover' />
              <Text fz={13} lineClamp={1} lh={1}>
                {RouterState?.restaurantInfo.fullAddress}
              </Text>
            </Flex>
          </Flex>
        </Box>

        <Text mt={16} c='white' fw={600}>
          SNACK ITEMS
        </Text>

        <Accordion
          defaultValue={String(RouterState.foodInfo.menu_item_id)}
          chevron={null}
          styles={(theme) => ({
            item: {
              border: 'none',
            },
            control: {
              padding: 0,
              ':hover': {
                background: 'transparent',
              },
            },
            chevron: {
              display: 'none',
            },

            content: {
              padding: 0,
            },

            label: {
              padding: 0,
            },
          })}>
          <Accordion.Item value={String(RouterState.foodInfo.menu_item_id)}>
            <Accordion.Control>
              <Box
                style={{
                  padding: 8,
                  border: '0.0625rem solid #dee2e6',
                  cursor: 'pointer',
                  borderRadius: 20,
                  background: 'white',
                }}>
                <Grid p={0} m={0}>
                  <Grid.Col className={(classes.genericGridCol, classes.firstGridCol)}>
                    <Image
                      src={RouterState?.foodInfo.image_url || DummyImage}
                      radius={12}
                      fit='cover'
                      width={'100%'}
                      height={75}
                    />
                  </Grid.Col>
                  <Grid.Col className={(classes.genericGridCol, classes.secondGridCol)}>
                    <Flex direction='column' gap={16} justify='center' h={'100%'}>
                      <Text fz={14} fw={500} lh={1}>
                        {RouterState?.foodInfo.name}
                      </Text>

                      <Flex gap={16}>
                        <Flex gap={4} align='center'>
                          {RouterState.foodInfo.diet.diet_id === 3 ? <RedDot /> : <GreenDot />}
                          <Text fz={12} lh={1} fw={500} c='dimmed'>
                            {RouterState?.foodInfo.diet.name}
                          </Text>
                        </Flex>

                        <Flex gap={4} align='center'>
                          <YellowDot />
                          <Text fz={12} lh={1} fw={500} c='dimmed'>
                            {RouterState?.foodInfo.calories} Cal
                          </Text>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Grid.Col>
                  <Grid.Col className={(classes.genericGridCol, classes.thirdGridCol)}>
                    <Flex direction='column' gap={16} justify='center' align='center' h='100%'>
                      <Text fz={18} fw={500} c='red' lh={1} ta='center'>
                        ${parseFloat(RouterState?.foodInfo.price).toFixed(2)}
                      </Text>
                      <Box
                        style={{
                          border: '1px solid #D61D4F',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: 4,
                          width: '100%',
                          borderRadius: 8,
                        }}>
                        <Text fz={11} lh={1} c='magenta.9' fw={600}>
                          Details
                        </Text>
                      </Box>
                    </Flex>
                  </Grid.Col>
                </Grid>
              </Box>
            </Accordion.Control>

            <Accordion.Panel>
              <Divider mt={12} />

              <Text fz={16} fw={600} mt={8}>
                Item Description
              </Text>
              <Text fz={13} fw={400}>
                {RouterState?.foodInfo.description}
              </Text>

              <Table mt={16} className={classes.table}>
                <thead>
                  <tr>
                    <th></th>
                    <th>
                      <Text>Item</Text>
                    </th>
                    <th>
                      <Text ta='center'>Unit Price</Text>
                    </th>
                    <th>
                      <Text ta='center'>Level</Text>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading &&
                    Array(1)
                      .fill(0)
                      .map((_, index) => (
                        <tr key={index} style={{ height: 45 }}>
                          <td></td>
                          <td>
                            <Skeleton h={16} w='32%' />
                          </td>
                          <td>
                            <Skeleton h={16} w='36%' mx='auto' />
                          </td>
                          <td>
                            <Box h={'100%'}>
                              <Skeleton h={16} w='88%' mx='auto' />
                            </Box>
                          </td>
                        </tr>
                      ))}

                  {isSuccess &&
                    data?.item_choices?.map((d, index: number) => (
                      <tr key={index}>
                        <td>
                          <Checkbox
                            onChange={(e) => {
                              handleCheckBoxClick({
                                e,
                                index,
                                amount: data.item_choices[index].addon_levels[record[index].currentValue].amount,
                              });
                            }}
                            radius='xs'
                            size='xs'
                            styles={(theme) => ({
                              input: {
                                cursor: 'pointer',
                              },
                            })}
                          />
                        </td>
                        <td>
                          <Text lh={1}>{d?.name}</Text>
                        </td>
                        <td>
                          <Text lh={1} ta='center'>
                            $ {data.item_choices[index]?.addon_levels[record[index]?.currentValue]?.amount?.toFixed(2)}
                          </Text>
                        </td>
                        <td style={{ display: 'flex', justifyContent: 'center' }}>
                          <Grid p={0} m={0} w='100%'>
                            <Grid.Col
                              className={cx(
                                classes.extraFoodChangerGenericGridCol,
                                classes.extraFoodChangerFirstGridCol,
                              )}
                              style={{ display: 'flex', justifyContent: 'center' }}>
                              <ActionIcon
                                disabled={record[index]?.currentValue === 0}
                                sx={(theme) => ({
                                  '&[data-disabled]': {
                                    pointerEvents: 'all',
                                    backgroundColor: 'transparent',
                                    border: 'none',

                                    '&:hover': {
                                      backgroundColor: 'transparent',
                                    },
                                  },

                                  '&:hover': {
                                    backgroundColor: theme.colors.orange[0],
                                    borderRadius: '50%',
                                  },
                                })}
                                onClick={() => {
                                  handleChange(
                                    index,
                                    'decrease',
                                    data?.item_choices[index].addon_levels[record[index].currentValue - 1].amount,
                                    data?.item_choices[index].addon_levels[record[index].currentValue - 1]
                                      .addon_level_id,
                                  );
                                }}>
                                <Box>
                                  <Image src={stepBackColor} />
                                </Box>
                              </ActionIcon>
                            </Grid.Col>
                            <Grid.Col
                              className={cx(
                                classes.extraFoodChangerGenericGridCol,
                                classes.extraFoodChangerSecondGridCol,
                              )}
                              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              {data?.item_choices[index]?.addon_levels[record[index]?.currentValue]?.name}
                            </Grid.Col>
                            <Grid.Col
                              className={cx(
                                classes.extraFoodChangerGenericGridCol,
                                classes.extraFoodChangerThirdGridCol,
                              )}
                              style={{ display: 'flex', justifyContent: 'center' }}>
                              <ActionIcon
                                disabled={record[index]?.currentValue + 1 >= record[index]?.maxValue}
                                sx={(theme) => ({
                                  '&[data-disabled]': {
                                    pointerEvents: 'all',
                                    backgroundColor: 'transparent',
                                    border: 'none',

                                    '&:hover': {
                                      backgroundColor: 'transparent',
                                    },
                                  },

                                  '&:hover': {
                                    backgroundColor: theme.colors.orange[0],
                                    borderRadius: '50%',
                                  },
                                })}
                                onClick={() =>
                                  handleChange(
                                    index,
                                    'increase',
                                    data?.item_choices[index].addon_levels[record[index].currentValue + 1].amount,
                                    data?.item_choices[index].addon_levels[record[index].currentValue + 1]
                                      .addon_level_id,
                                  )
                                }>
                                <Box>
                                  <Image src={stepForwardColor} />
                                </Box>
                              </ActionIcon>
                            </Grid.Col>
                          </Grid>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>

              <Textarea
                mt={8}
                autosize
                placeholder='Type in ...'
                label='Request to chef'
                spellCheck={false}
                value={requestToChefNote}
                onChange={(event) => setRequestToChefNote(event.currentTarget.value)}
              />

              <Flex justify='space-between' mt={12}>
                <Grid className={classes.foodQuantityChangerGrid}>
                  <Grid.Col
                    className={cx(classes.foodQuantityChangerGenericGridCol, classes.foodQuantityChangerFirstGridCol)}
                    // xs={4}
                    p={0}
                    m={0}
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ActionIcon
                      disabled={totalPrice.quantity === 1}
                      sx={(theme) => ({
                        ':hover': {
                          backgroundColor: 'transparent',
                        },

                        '&[data-disabled]': { pointerEvents: 'all', backgroundColor: 'transparent', border: 'none' },
                      })}
                      onClick={() => handleFoodQuantityChange('decrease', 1)}>
                      <Box>
                        <Image src={removeIcon} />
                      </Box>
                    </ActionIcon>
                  </Grid.Col>
                  <Grid.Col
                    // xs={4}
                    className={cx(classes.foodQuantityChangerGenericGridCol, classes.foodQuantityChangerSecondGridCol)}
                    p={0}
                    m={0}
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Text>{totalPrice.quantity}</Text>
                  </Grid.Col>
                  <Grid.Col
                    // xs={4}
                    className={cx(classes.foodQuantityChangerGenericGridCol, classes.foodQuantityChangerThirdGridCol)}
                    p={0}
                    m={0}
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ActionIcon
                      disabled={totalPrice.quantity === 10}
                      sx={(theme) => ({
                        ':hover': {
                          backgroundColor: 'transparent',
                        },

                        '&[data-disabled]': { pointerEvents: 'all', backgroundColor: 'transparent', border: 'none' },
                      })}
                      onClick={() => handleFoodQuantityChange('increase', 1)}>
                      <Box>
                        <Image src={addIcon} />
                      </Box>
                    </ActionIcon>
                  </Grid.Col>
                </Grid>

                <Button
                  radius={8}
                  w={160}
                  loading={addToCardIsLoading}
                  loaderPosition='center'
                  disabled={isLoading}
                  onClick={handleAdd}
                  styles={(theme) => ({
                    root: {
                      backgroundColor: theme.colors.red[6],
                      '&:hover': {
                        backgroundColor: theme.colors.red[5],
                      },
                    },
                  })}>
                  <Text lh={1}>
                    {data === undefined ? (
                      'Loading'
                    ) : (
                      <>
                        {data.is_in_cart ? (
                          <Text>Go To Cart</Text>
                        ) : (
                          <Text lh={1}>
                            {addToCardIsLoading ? null : `ADD | $ ${totalPrice.totalPrice.toFixed(2)}`}
                          </Text>
                        )}
                      </>
                    )}
                  </Text>
                </Button>
              </Flex>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Container>
    </>
  );
};

export default AddToCartRefactored;
